/**
 * Konaklama bölümü: bir tarih aralığı, otel listesi, "Fiyatları çek", sonuç tablosu ve oda tipi şeritleri.
 * Teklif bir ya da birden çok ardışık bölümden oluşur (Musa kararı 26.10.2026). Tek bölümde ekran Alternatif A'dır
 * (satır = otel seçeneği, birden çok işaretlenebilir); çoklu bölümde her bölümde TEK seçenek seçilir (radyo) ve üst bileşen
 * seçimleri toplar. Her bölüm kendi fiyat işini, sırasını ve süre izlemesini yürütür; ortak olanlar (müşteri, oda
 * dağılımı, paket, mesaj, sürüm) üst bileşendedir ve bölüm `rapor` ile durumunu yukarı bildirir.
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { api, ApiError, type HotelPictureView } from '../api';
import { HIZLI_METIN as M } from '../metin/hizliTeklif';
import type { OtelOzeti } from '../otelTeklif/tipler';
import { TarihAraligiSecici } from '../TarihSecici';
import { hizliTeklifApi } from './istemci';
import { Ikon } from './Ikon';
import {
  POLITIKA,
  EsikTakibi,
  manuelDegerlendir,
  yeniManuelOtel,
  fiyataGoreSirala,
  gunSayisi,
  odaDuzenleri,
  secilenOdaAdlari,
  otelEkle,
  otelGirdisiOlustur,
  sayimMetni,
  sayimla,
  siraKarari,
  siraUygula,
  sonucBul,
  sureMetni,
  talepImzasi,
  yeniOdaKimligi,
  satirDurumu,
  satirHesapla,
  tutarCoz,
  varsayilanSecenekNo,
  type BolumTarihHatasi,
  type SatirDurumu,
  type Secim,
} from './mantik';
import { OdaTipiSeridi } from './OdaTipiSeridi';
import { SatisFiyatiAlani } from './SatisFiyatiAlani';
import { otelFiyatiniOlcekle } from '../../../api/src/modules/hizli-teklif/otel-fiyat-olcekle';
import { ManuelOtelFormu, ManuelOtelListesi } from './ManuelOtelAlani';
import { OtelSecici } from './OtelSecici';
import { SonucTablosu, type SatirVeri } from './SonucTablosu';
import type { AlternatifDagilim, DurumKodu, FiyatSonucu, Ilerleme, ManuelOtel, OdaGrubu, OdaTaslagi, OtelGirdisi, OtelSonucu, TaslakBolum } from './tipler';
import type { MusteriBilgisi } from './MusteriAlani';

interface Calisma {
  readonly seq: number;
  readonly isId: string | null;
  readonly imza: string;
  readonly durum: 'bos' | 'cekiliyor' | 'bitti' | 'hata';
  readonly oteller: readonly OtelGirdisi[];
  readonly biten: Readonly<Record<string, OtelSonucu>>;
  readonly ilerleme: Ilerleme | null;
  readonly sonuc: FiyatSonucu | null;
  readonly hata: string | null;
  readonly baslangic: number;
  readonly bitis: number | null;
}
const BOS_CALISMA: Calisma = { seq: 0, isId: null, imza: '', durum: 'bos', oteller: [], biten: {}, ilerleme: null, sonuc: null, hata: null, baslangic: 0, bitis: null };

export interface BolumSatiri {
  /** Satışçının "Ek not"u (düz metin). */
  readonly ekNot?: string;
  /** Teklife özel satış fiyatı (seçilen odaların toplamı, minor); `sonuc` zaten bu fiyatla ölçeklenmiş gelir. */
  readonly satisToplamMinor?: number;
  /** Bu satırın fiyatının geldiği iş (tek otel yenilemesi bölümün işinden farklı olabilir). */
  readonly isId: string | null;
  readonly otel: OtelGirdisi;
  readonly sonuc: OtelSonucu;
  readonly durum: SatirDurumu;
}

/** Teklife giren katalogda olmayan otel: toplam tarihlerden hesaplanır (gecelik tutarda tarih yoksa null). */
export interface BolumManuelSatiri {
  readonly manuel: ManuelOtel;
  readonly toplamMinor: number | null;
  readonly gecerli: boolean;
}

/** Bölümün üst bileşene bildirdiği durum. `taslak` sürüm kaydı içindir. */
export interface BolumRaporu {
  readonly anahtar: string;
  readonly oteller: readonly OtelGirdisi[];
  /** Fiyat işi ya da tek otel yenilemesi sürüyor. */
  readonly calisiyor: boolean;
  readonly cekimBitti: boolean;
  readonly sonucVar: boolean;
  readonly eskiTalep: boolean;
  readonly isaretli: readonly BolumSatiri[];
  /** Teklife giren katalogda olmayan oteller (28.10.2026). */
  readonly manuelIsaretli: readonly BolumManuelSatiri[];
  readonly sayim: ReturnType<typeof sayimla>;
  readonly taslak: TaslakBolum;
  /** Bölümün fiyat işi. */
  readonly isId: string | null;
  readonly engel: string | null;
  /** Bölümün fiyatlarını çeker (engel varsa duyurur). */
  readonly cek: () => void;
}

export function Bolum({
  kimlikKok,
  indeks,
  anahtar,
  etiket,
  coklu,
  aktif,
  giris,
  cikis,
  setAralik,
  tarihHata,
  tarihBolumdeGoster,
  odalar,
  yasTamam,
  odalariUygula,
  musteri,
  uyruk,
  salt,
  yan,
  silinebilir,
  sil,
  duyur,
  rapor,
  bitti,
  baslangic,
}: {
  kimlikKok: string;
  indeks: number;
  anahtar: string;
  etiket: string;
  coklu: boolean;
  aktif: boolean;
  giris: string;
  cikis: string;
  setAralik: (g: string, c: string) => void;
  tarihHata: BolumTarihHatasi;
  tarihBolumdeGoster: boolean;
  odalar: readonly OdaTaslagi[];
  yasTamam: boolean;
  odalariUygula: (yeni: OdaTaslagi[]) => void;
  musteri: MusteriBilgisi | null;
  uyruk: string;
  salt: TaslakBolum | null;
  yan: ReactNode;
  silinebilir: boolean;
  sil: () => void;
  duyur: (m: string) => void;
  rapor: (r: BolumRaporu) => void;
  bitti: () => void;
  /** Kayıtlı taslaktan geri kurulan bölüm durumu; verilince canlı durum bununla doldurulur (düzenlenebilir). */
  baslangic: TaslakBolum | null;
}) {
  const kimlik = `${kimlikKok}-${anahtar}`;
  const onek = indeks === 0 ? 'ht' : `ht-b${indeks}`;
  const tid = useId();
  const [oteller, setOteller] = useState<OtelGirdisi[]>([]);
  const [cal, setCal] = useState<Calisma>(BOS_CALISMA);
  const [ek, setEk] = useState<Readonly<Record<string, { readonly sonuc: OtelSonucu; readonly imza: string; readonly isId: string }>>>({});
  const [yenilenen, setYenilenen] = useState<readonly string[]>([]);
  const [isaretli, setIsaretli] = useState<ReadonlySet<string>>(new Set());
  const [secimler, setSecimler] = useState<Readonly<Record<string, Secim>>>({});
  const [ekNotlar, setEkNotlar] = useState<Readonly<Record<string, string>>>({});
  const [satisFiyatlari, setSatisFiyatlari] = useState<Readonly<Record<string, string>>>({});
  const [acikSeritler, setAcikSeritler] = useState<ReadonlySet<string>>(new Set());
  const [yeniGelen, setYeniGelen] = useState<ReadonlySet<string>>(new Set());
  const [sira, setSira] = useState<readonly string[] | null>(null);
  const [kapaklar, setKapaklar] = useState<Readonly<Record<string, HotelPictureView | null>>>({});
  const [manuel, setManuel] = useState<readonly ManuelOtel[]>([]);
  const [manuelIsaretli, setManuelIsaretli] = useState<ReadonlySet<string>>(new Set());
  const [manuelForm, setManuelForm] = useState<ManuelOtel | null>(null);
  const [simdi, setSimdi] = useState(() => Date.now());
  const [baslatHata, setBaslatHata] = useState<string | null>(null);
  const [silSor, setSilSor] = useState(false);
  const esik = useRef(new EsikTakibi());
  const sonEtkilesim = useRef<number | null>(null);
  const siralandiSeq = useRef(0);
  const sonucBolumu = useRef<HTMLElement>(null);
  const duyurulanHata = useRef(new Set<string>());
  const duyurulanIlerleme = useRef(0);
  const bagli = useRef(true);
  useEffect(() => { bagli.current = true; return () => { bagli.current = false; }; }, []);

  const dy = useCallback((m: string) => duyur(coklu ? `${etiket}: ${m}` : m), [duyur, coklu, etiket]);

  const talepIm = useMemo(() => talepImzasi({ giris, cikis, odalar, uyruk }), [giris, cikis, odalar, uyruk]);
  const gece = gunSayisi(giris, cikis);
  const saltOkunur = salt !== null;

  // Taslağı geri kur (03.11.2026): kayıtlı oteller, fiyat sonuçları, seçimler ve satış fiyatları canlı duruma yazılır.
  // Fiyat sonucu talep imzasıyla işaretlenir; "Fiyatları çek" aynı talepte seçimleri korur, süre dolmuşsa satır uyarı verir.
  const uygulananBaslangic = useRef<TaslakBolum | null>(null);
  useEffect(() => {
    if (baslangic === null || uygulananBaslangic.current === baslangic) return;
    uygulananBaslangic.current = baslangic;
    const biten: Record<string, OtelSonucu> = {};
    for (const s of baslangic.sonuc?.oteller ?? []) biten[s.girdi] = s;
    const simdiMs = Date.now();
    setOteller([...baslangic.oteller]);
    setCal({ ...BOS_CALISMA, seq: baslangic.sonuc === null ? 0 : 1, durum: baslangic.sonuc === null ? 'bos' : 'bitti', oteller: baslangic.oteller, biten, sonuc: baslangic.sonuc, imza: baslangic.sonuc === null ? '' : talepIm, baslangic: simdiMs, bitis: baslangic.sonuc === null ? null : simdiMs });
    setEk({}); setYenilenen([]); setYeniGelen(new Set()); setSira(null); setAcikSeritler(new Set());
    setIsaretli(new Set(baslangic.isaretli));
    setSecimler(baslangic.secimler as Readonly<Record<string, Secim>>);
    setEkNotlar(baslangic.ekNotlar ?? {});
    setSatisFiyatlari(baslangic.satisFiyatlari ?? {});
    setManuel([...(baslangic.manuel ?? [])]);
    setManuelIsaretli(new Set(baslangic.manuelIsaretli ?? []));
  }, [baslangic, talepIm]);

  // Görünüm: canlı ya da salt okunur sürüm.
  const G = useMemo(() => {
    if (salt === null) return { cal, oteller, isaretli, secimler, ekNotlar, satisFiyatlari, manuel, manuelIsaretli };
    const biten: Record<string, OtelSonucu> = {};
    for (const s of salt.sonuc?.oteller ?? []) biten[s.girdi] = s;
    const c: Calisma = { ...BOS_CALISMA, seq: -1, durum: 'bitti', oteller: salt.oteller, biten, sonuc: salt.sonuc, imza: 'surum' };
    return { cal: c, oteller: [...salt.oteller], isaretli: new Set(salt.isaretli) as ReadonlySet<string>, secimler: salt.secimler as Readonly<Record<string, Secim>>, ekNotlar: salt.ekNotlar ?? {}, satisFiyatlari: salt.satisFiyatlari ?? {}, manuel: salt.manuel ?? [], manuelIsaretli: new Set(salt.manuelIsaretli ?? []) as ReadonlySet<string> };
  }, [salt, cal, oteller, isaretli, secimler, ekNotlar, satisFiyatlari, manuel, manuelIsaretli]);

  const eskiTalep = salt === null && G.cal.imza !== '' && G.cal.imza !== talepIm;
  const calisiyor = G.cal.durum === 'cekiliyor';
  const manuelSatirlari: BolumManuelSatiri[] = G.manuel.filter((m) => G.manuelIsaretli.has(m.id)).map((m) => {
    const d = manuelDegerlendir(m, gece);
    return { manuel: m, toplamMinor: d.toplamMinor, gecerli: d.hata === null && d.toplamMinor !== null };
  });
  const cekimBitti = (G.cal.durum === 'bitti' || G.cal.durum === 'hata') && yenilenen.length === 0;

  const sonucMap = useMemo(() => {
    const m: Record<string, OtelSonucu> = { ...G.cal.biten };
    for (const [k, v] of Object.entries(ek)) if (v.imza === G.cal.imza) m[k] = v.sonuc;
    return m;
  }, [G.cal.biten, G.cal.imza, ek]);

  const tabloOtelleri = useMemo(() => G.cal.oteller.filter((o) => G.oteller.some((x) => x.girdi === o.girdi)), [G.cal.oteller, G.oteller]);
  const sirali = useMemo(() => siraUygula(tabloOtelleri, salt === null ? sira : null), [tabloOtelleri, sira, salt]);

  const bekleyenSirasi = useCallback((girdi: string): number | null => {
    if (!calisiyor && !yenilenen.includes(girdi)) return null;
    const bekleyenler = sirali.filter((o) => sonucMap[o.girdi] === undefined || yenilenen.includes(o.girdi));
    const i = bekleyenler.findIndex((o) => o.girdi === girdi);
    return i < 0 ? null : i;
  }, [calisiyor, yenilenen, sirali, sonucMap]);

  const satirlar = useMemo(() => sirali.map((o) => {
    const sonuc = yenilenen.includes(o.girdi) ? null : (sonucBul(Object.values(sonucMap), o) ?? null);
    const secim = G.secimler[o.girdi] ?? {};
    // Teklife özel satış fiyatı: sistem sonucunu aynı saf fonksiyonla ölçekler (sunucu PDF'te de bunu çalıştırır).
    const yazilan = G.satisFiyatlari[o.girdi]?.trim() ?? '';
    const hedef = yazilan === '' ? null : tutarCoz(yazilan);
    const olcekli = sonuc !== null && hedef !== null ? otelFiyatiniOlcekle(sonuc, secim, hedef) : null;
    const goruntulenen = olcekli?.tamam === true ? olcekli.otel : sonuc;
    const durum = satirDurumu({ otel: o, sonuc: goruntulenen, secim, bekleyenSirasi: sonuc === null ? (bekleyenSirasi(o.girdi) ?? (yenilenen.includes(o.girdi) ? 0 : null)) : null, eskiTalep, simdi }, yeniGelen);
    const sistemHesap = sonuc === null ? null : satirHesapla(sonuc, secim);
    return {
      otel: o, sonuc: goruntulenen, secim, durum,
      satisToplamMinor: olcekli?.tamam === true && hedef !== null ? hedef : undefined,
      satisHatali: yazilan !== '' && sonuc !== null && olcekli?.tamam !== true,
      sistemMinor: sistemHesap?.toplamMinor ?? null,
      sistemPara: sistemHesap?.paraBirimi ?? null,
      odaAdlari: sistemHesap === null ? undefined : secilenOdaAdlari(sistemHesap.secilenler, odalar.length),
    };
  }), [sirali, sonucMap, G.secimler, G.satisFiyatlari, bekleyenSirasi, eskiTalep, simdi, yeniGelen, yenilenen, odalar.length]);

  const durumHaritasi = useMemo(() => {
    const h: Record<string, DurumKodu | undefined> = {};
    if (G.cal.durum !== 'bos') for (const s of satirlar) h[s.otel.girdi] = s.durum.kod;
    return h;
  }, [satirlar, G.cal.durum]);
  const sayim = useMemo(() => sayimla(satirlar.map((s) => s.durum.kod)), [satirlar]);

  const fiyatliToplamlar = satirlar.flatMap((s) => (s.durum.secilebilir && s.durum.hesap?.toplamMinor !== undefined && s.durum.hesap.toplamMinor !== null ? [s.durum.hesap.toplamMinor] : []));
  const enDusukMinor = fiyatliToplamlar.length > 1 && cekimBitti ? Math.min(...fiyatliToplamlar) : null;
  const isaretliSatirlar: BolumSatiri[] = satirlar.flatMap((s) => (G.isaretli.has(s.otel.girdi) && s.sonuc !== null && s.durum.kod !== 'eski' ? [{ ...(G.ekNotlar[s.otel.girdi] === undefined || G.ekNotlar[s.otel.girdi]?.trim() === '' ? {} : { ekNot: G.ekNotlar[s.otel.girdi] as string }), ...(s.satisToplamMinor === undefined ? {} : { satisToplamMinor: s.satisToplamMinor }), isId: (ek[s.otel.girdi]?.imza === G.cal.imza ? ek[s.otel.girdi]?.isId : null) ?? G.cal.isId, otel: s.otel, sonuc: s.sonuc, durum: s.durum }] : []));

  // ---- engel ve fiyat çekme ----
  const engelMetni = useMemo((): string | null => {
    if (saltOkunur) return M.engelSurum;
    if (calisiyor || yenilenen.length > 0) return M.engelSurer;
    if (musteri === null) return M.engelMusteri;
    if (uyruk === '') return M.engelUyruk;
    if (tarihHata === 'ortusme') return M.engelOrtusme;
    if (gece === null) return M.engelTarih;
    if (!yasTamam) return M.engelYas;
    if (oteller.length === 0) return M.engelOtelYok;
    return null;
  }, [saltOkunur, calisiyor, yenilenen.length, musteri, uyruk, tarihHata, gece, yasTamam, oteller.length]);

  const fiyatCek = useCallback((odaGecersiz?: readonly OdaTaslagi[]) => {
    const o = odaGecersiz ?? odalar;
    if (engelMetni !== null && odaGecersiz === undefined) { dy(engelMetni); return; }
    if (musteri === null || uyruk === '') return;
    const imza = talepImzasi({ giris, cikis, odalar: o, uyruk });
    const yeniSeq = cal.seq + 1;
    if (cal.imza !== imza) { setIsaretli(new Set()); setSecimler({}); setSatisFiyatlari({}); }
    setEk({}); setYenilenen([]); setBaslatHata(null); setYeniGelen(new Set()); setSira(null); setAcikSeritler(new Set());
    esik.current.sifirla(); duyurulanHata.current = new Set(); duyurulanIlerleme.current = 0;
    const secilenOteller = oteller.map((x) => x);
    setCal({ ...BOS_CALISMA, seq: yeniSeq, imza, durum: 'cekiliyor', oteller: secilenOteller, baslangic: Date.now(), ilerleme: { toplam: secilenOteller.length, biten: 0, hatali: 0, bekleyen: secilenOteller.map((x) => x.girdi) } });
    dy(M.duy.basla(secilenOteller.length));
    hizliTeklifApi.baslat({ girisTarihi: giris, cikisTarihi: cikis, odalar: odaDuzenleri(o), otelIdleri: secilenOteller.map((x) => x.girdi), uyruk, mod: 'ODA_BAZLI' })
      .then(({ id }) => setCal((c) => (c.seq === yeniSeq ? { ...c, isId: id } : c)))
      .catch((e: unknown) => {
        const ileti = e instanceof ApiError ? e.message : M.baslatilamadi;
        setBaslatHata(ileti);
        setCal((c) => (c.seq === yeniSeq ? { ...c, durum: 'hata', hata: ileti, bitis: Date.now() } : c));
      });
  }, [odalar, engelMetni, dy, musteri, uyruk, giris, cikis, cal.seq, cal.imza, oteller]);

  // Yoklama: işin durumunu 1 sn'de bir okur; eski işin geç yanıtı yeni çalışmaya yazılmaz (seq).
  useEffect(() => {
    if (cal.durum !== 'cekiliyor' || cal.isId === null) return undefined;
    const seq = cal.seq;
    const id = cal.isId;
    const c = new AbortController();
    let olu = false;
    let ardisikHata = 0;
    const bilinen = new Set<string>();
    const basla = Date.now();
    const dongu = async () => {
      while (!olu) {
        try {
          const d = await hizliTeklifApi.durum(id, c.signal);
          if (olu) return;
          ardisikHata = 0;
          const biten: Record<string, OtelSonucu> = {};
          for (const s of d.biten) biten[s.girdi] = s;
          if (d.sonuc !== null) for (const s of d.sonuc.oteller) biten[s.girdi] = s;
          const yeniler = Object.keys(biten).filter((g) => !bilinen.has(g));
          for (const g of yeniler) bilinen.add(g);
          if (yeniler.length > 0) setYeniGelen((y) => new Set([...y, ...yeniler]));
          setCal((k) => {
            if (k.seq !== seq) return k;
            if (d.durum === 'BITTI') return { ...k, biten, ilerleme: d.ilerleme, sonuc: d.sonuc, durum: 'bitti', bitis: Date.now() };
            if (d.durum === 'HATA') return { ...k, biten, ilerleme: d.ilerleme, durum: 'hata', hata: d.hata ?? M.baslatilamadi, bitis: Date.now() };
            return { ...k, biten, ilerleme: d.ilerleme };
          });
          if (d.durum !== 'CALISIYOR') return;
        } catch (e) {
          if (olu || c.signal.aborted) return;
          ardisikHata += 1;
          if (e instanceof ApiError && e.status === 404) { setCal((k) => (k.seq === seq ? { ...k, durum: 'hata', hata: M.isKayboldu, bitis: Date.now() } : k)); return; }
          if (ardisikHata >= 4 || Date.now() - basla > 150_000) { setCal((k) => (k.seq === seq ? { ...k, durum: 'hata', hata: M.baslatilamadi, bitis: Date.now() } : k)); return; }
        }
        await new Promise((r) => window.setTimeout(r, POLITIKA.yoklamaMs));
      }
    };
    void dongu();
    return () => { olu = true; c.abort(); };
  }, [cal.seq, cal.isId, cal.durum]);

  useEffect(() => {
    if (salt !== null || cal.durum === 'bos') return;
    const biten = Object.keys(cal.biten).length;
    if (cal.durum === 'cekiliyor' && biten >= duyurulanIlerleme.current + 2 && biten < (cal.ilerleme?.toplam ?? 0)) {
      duyurulanIlerleme.current = biten;
      dy(M.duy.ilerleme(biten, cal.ilerleme?.toplam ?? cal.oteller.length));
    }
    for (const s of Object.values(cal.biten)) {
      if (s.durum === 'HATA' && s.hata !== null && s.hata.yenidenDenenebilir && !duyurulanHata.current.has(s.girdi)) {
        duyurulanHata.current.add(s.girdi);
        dy(M.duy.hata(s.otelAdi ?? s.girdi));
      }
    }
  }, [cal.biten, cal.durum, cal.ilerleme, cal.oteller.length, salt, dy]);

  // Çekim bitince: bir kez sayım duyurusu, bir kez sıralama kararı, sürüm kaydı isteği.
  const bitisIsledi = useRef(0);
  useEffect(() => {
    if (salt !== null || cal.durum !== 'bitti' || bitisIsledi.current === cal.seq) return;
    bitisIsledi.current = cal.seq;
    dy(M.duy.bitti(sayimMetni(sayim, M.sayim)));
    const karar = siraKarari({ cekimBitti: true, zatenSiralandi: siralandiSeq.current === cal.seq, sonEtkilesim: sonEtkilesim.current, simdi: Date.now() });
    if (karar === 'sirala') {
      siralandiSeq.current = cal.seq;
      setSira(fiyataGoreSirala(cal.oteller.map((o) => {
        const s = sonucBul(Object.values(cal.biten), o);
        return { girdi: o.girdi, toplamMinor: s === null || s.durum === 'HATA' ? null : s.enUcuzMinor };
      })));
      dy(M.duy.sira);
    }
    bitti();
    // Bir çekim bir kez işlenir (`bitisIsledi`); sayım ve bitti() o anki render'dan okunur.
  }, [cal.durum, cal.seq]);

  const fiyatSirasi = useMemo(() => fiyataGoreSirala(sirali.map((o) => ({ girdi: o.girdi, toplamMinor: satirlar.find((s) => s.otel.girdi === o.girdi)?.durum.hesap?.toplamMinor ?? null }))), [sirali, satirlar]);
  const siraFarkli = cekimBitti && salt === null && fiyatSirasi.join('|') !== sirali.map((o) => o.girdi).join('|');

  const sonucVar = satirlar.some((s) => s.sonuc !== null) || manuelSatirlari.length > 0;
  const fiyatVar = satirlar.some((s) => s.sonuc !== null);
  useEffect(() => {
    if (!fiyatVar) return undefined;
    const t = window.setInterval(() => setSimdi(Date.now()), 5_000);
    return () => window.clearInterval(t);
  }, [fiyatVar]);
  useEffect(() => {
    for (const s of satirlar) {
      if (!G.isaretli.has(s.otel.girdi)) continue;
      const e = esik.current.kontrol(s.otel.girdi, s.durum.sure);
      const ad = s.sonuc?.otelAdi ?? s.otel.ad;
      if (e === 'bilgi') dy(M.duy.sure(ad, 30));
      else if (e === 'kritik') dy(M.duy.sure(ad, 2));
      else if (e === 'doldu') dy(M.duy.sureDoldu(ad));
    }
  }, [satirlar, G.isaretli, dy]);

  // Talep değişti: onaylar ve oda tipi seçimleri geçersiz (şartname 9). Otel tercihi kalır.
  const eskiDuyuruldu = useRef(false);
  useEffect(() => {
    if (!eskiTalep) { eskiDuyuruldu.current = false; return; }
    setIsaretli((s) => (s.size === 0 ? s : new Set()));
    setSecimler((s) => (Object.keys(s).length === 0 ? s : {}));
    setSatisFiyatlari((s) => (Object.keys(s).length === 0 ? s : {}));
    setAcikSeritler((s) => (s.size === 0 ? s : new Set()));
    if (!eskiDuyuruldu.current) { eskiDuyuruldu.current = true; dy(M.duy.eski); }
  }, [eskiTalep, dy]);

  // Otel kapakları: yalnız aynı slug'ın katalog kapağı (kısmi eşleşme kanıt değil); yoksa baş harfler.
  const kapakIstendi = useRef(new Set<string>());
  useEffect(() => {
    for (const s of satirlar) {
      const slug = s.sonuc?.slug ?? s.otel.slug;
      if (s.sonuc === null || kapakIstendi.current.has(slug)) continue;
      kapakIstendi.current.add(slug);
      api.hotelRoomPhotos(slug).then((v) => { if (bagli.current) setKapaklar((k) => ({ ...k, [slug]: v.cover })); }).catch(() => undefined);
    }
  }, [satirlar]);

  // ---- etkileşimler ----
  const etkilesim = useCallback(() => { sonEtkilesim.current = Date.now(); }, []);

  const aramadanEkle = useCallback((o: OtelOzeti): 'eklendi' | 'var' | 'dolu' => {
    const r = otelEkle(oteller, otelGirdisiOlustur(o));
    if (r.sonuc === 'eklendi') { setOteller([...r.liste]); dy(M.otelEklendi(o.ad)); }
    return r.sonuc;
  }, [oteller, dy]);

  const otelKaldir = useCallback((girdi: string) => {
    setOteller((l) => l.filter((o) => o.girdi !== girdi));
    setIsaretli((s) => { const n = new Set(s); n.delete(girdi); return n; });
  }, []);

  const tekYenile = useCallback((girdiler: readonly string[]) => {
    if (salt !== null || musteri === null || cal.seq === 0) return;
    const imza = cal.imza;
    if (imza !== talepIm) { dy(M.engelSurer); return; }
    const hedef = cal.oteller.filter((o) => girdiler.includes(o.girdi));
    if (hedef.length === 0) return;
    setYenilenen((l) => [...new Set([...l, ...hedef.map((o) => o.girdi)])]);
    for (const o of hedef) esik.current.sifirla(o.girdi);
    hizliTeklifApi.baslat({ girisTarihi: giris, cikisTarihi: cikis, odalar: odaDuzenleri(odalar), otelIdleri: hedef.map((o) => o.girdi), uyruk, mod: 'ODA_BAZLI' })
      .then(async ({ id }) => {
        for (let i = 0; i < 90; i += 1) {
          const d = await hizliTeklifApi.durum(id);
          if (d.durum !== 'CALISIYOR') {
            const sonuclar = d.sonuc?.oteller ?? d.biten;
            setEk((e) => { const n = { ...e }; for (const s of sonuclar) n[s.girdi] = { sonuc: s, imza, isId: id }; return n; });
            setSecimler((s) => { const n = { ...s }; for (const o of hedef) delete n[o.girdi]; return n; });
            setSatisFiyatlari((s) => { const n = { ...s }; for (const o of hedef) delete n[o.girdi]; return n; });
            setYeniGelen((y) => new Set([...y, ...sonuclar.map((s) => s.girdi)]));
            return;
          }
          await new Promise((r) => window.setTimeout(r, POLITIKA.yoklamaMs));
        }
      })
      .catch(() => dy(M.baslatilamadi))
      .finally(() => setYenilenen((l) => l.filter((g) => !hedef.some((o) => o.girdi === g))));
  }, [salt, musteri, cal.seq, cal.imza, cal.oteller, talepIm, giris, cikis, odalar, uyruk, dy]);

  const dagilimUygula = useCallback((grup: OdaGrubu, d: AlternatifDagilim) => {
    const yeni: OdaTaslagi[] = [];
    odalar.forEach((o, i) => {
      if (!grup.odaNolari.includes(i + 1)) { yeni.push(o); return; }
      for (const r of d.odalar) yeni.push({ id: yeniOdaKimligi(), yetiskin: r.yetiskin, cocukYaslari: r.cocukYaslari.map(String) });
    });
    odalariUygula(yeni);
    // Uygulanınca fiyat yeniden çekilir (şartname 6).
    fiyatCek(yeni);
  }, [odalar, odalariUygula, fiyatCek]);

  const seritDegistir = useCallback((girdi: string) => {
    etkilesim();
    setYeniGelen((y) => { if (!y.has(girdi)) return y; const n = new Set(y); n.delete(girdi); return n; });
    setAcikSeritler((s) => { const n = new Set(s); if (n.has(girdi)) n.delete(girdi); else n.add(girdi); return n; });
  }, [etkilesim]);

  const seritKapat = useCallback((girdi: string) => {
    setAcikSeritler((s) => { const n = new Set(s); n.delete(girdi); return n; });
    window.requestAnimationFrame(() => document.querySelector<HTMLElement>(`[data-testid="${onek}-serit-dugme-${girdi}"]`)?.focus());
  }, [onek]);

  const isaretle = useCallback((girdi: string, deger: boolean) => {
    etkilesim();
    setYeniGelen((y) => { if (!y.has(girdi)) return y; const n = new Set(y); n.delete(girdi); return n; });
    // Çoklu bölümde her bölümde tek seçenek: yenisi seçilince öncekiler (katalogdaki ve elle eklenen) kalkar.
    setIsaretli((s) => { const n = coklu ? new Set<string>() : new Set(s); if (deger) n.add(girdi); else n.delete(girdi); return n; });
    if (coklu && deger) setManuelIsaretli(new Set());
  }, [etkilesim, coklu]);

  const manuelIsaretle = useCallback((id: string, deger: boolean) => {
    etkilesim();
    setManuelIsaretli((s) => { const n = coklu ? new Set<string>() : new Set(s); if (deger) n.add(id); else n.delete(id); return n; });
    if (coklu && deger) setIsaretli(new Set());
  }, [etkilesim, coklu]);

  const manuelKaydet = useCallback((m: ManuelOtel) => {
    setManuel((l) => (l.some((x) => x.id === m.id) ? l.map((x) => (x.id === m.id ? m : x)) : [...l, m]));
    setManuelForm(null);
    dy(M.manuel.eklendi(m.ad));
  }, [dy]);

  const manuelKaldir = useCallback((id: string) => {
    setManuel((l) => l.filter((x) => x.id !== id));
    setManuelIsaretli((s) => { const n = new Set(s); n.delete(id); return n; });
  }, []);

  const secimDegisti = useCallback((girdi: string, grup: string, no: string) => {
    etkilesim();
    setSecimler((s) => ({ ...s, [girdi]: { ...(s[girdi] ?? {}), [grup]: no } }));
    // Oda seçimi değişti: eski toplam artık başka odalara ait, yeniden yazılması gerekir.
    setSatisFiyatlari((m) => { if (m[girdi] === undefined) return m; const n = { ...m }; delete n[girdi]; return n; });
  }, [etkilesim]);

  const tusBasildi = useCallback((girdi: string, e: KeyboardEvent) => {
    if (e.key.toLowerCase() !== 'r' || e.ctrlKey || e.metaKey || e.altKey) return;
    const t = e.target as HTMLElement;
    const yaziAlani = t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || (t.tagName === 'INPUT' && (t as HTMLInputElement).type !== 'checkbox' && (t as HTMLInputElement).type !== 'radio');
    if (yaziAlani || t.isContentEditable) return;
    e.preventDefault();
    tekYenile([girdi]);
  }, [tekYenile]);

  const sonuclaraGit = useCallback(() => {
    const el = sonucBolumu.current;
    if (el === null) return;
    el.scrollIntoView({ block: 'start' });
    el.querySelector<HTMLElement>('h2')?.focus();
  }, []);

  // ---- rapor: imza değişince üst bileşene bildirilir (döngü yok: yalnız imza değişimiyle) ----
  const raporImzasi = JSON.stringify([
    cekimBitti, calisiyor, sonucVar, eskiTalep, G.oteller.map((o) => o.girdi),
    isaretliSatirlar.map((s) => [s.otel.girdi, s.isId, s.durum.kod, s.durum.sure, s.durum.hesap?.toplamMinor ?? null, s.durum.bitis]),
    G.cal.isId, Math.floor(simdi / 5000), talepIm, yenilenen.length, Object.keys(G.secimler).length, JSON.stringify(G.ekNotlar), JSON.stringify(G.satisFiyatlari),
    JSON.stringify(G.manuel), [...G.manuelIsaretli], gece,
  ]);
  const taslak: TaslakBolum = { anahtar, girisTarihi: giris, cikisTarihi: cikis, oteller: G.cal.oteller, sonuc: G.cal.sonuc, isaretli: [...G.isaretli], secimler: G.secimler, ...(Object.keys(G.ekNotlar).length === 0 ? {} : { ekNotlar: G.ekNotlar }), ...(Object.keys(G.satisFiyatlari).length === 0 ? {} : { satisFiyatlari: G.satisFiyatlari }), ...(G.manuel.length === 0 ? {} : { manuel: G.manuel, manuelIsaretli: [...G.manuelIsaretli] }) };
  useEffect(() => {
    rapor({ anahtar, oteller: G.oteller, calisiyor: calisiyor || yenilenen.length > 0, cekimBitti, sonucVar, eskiTalep, isaretli: isaretliSatirlar, manuelIsaretli: manuelSatirlari, sayim, taslak, isId: G.cal.isId, engel: engelMetni, cek: () => fiyatCek() });
  }, [raporImzasi, engelMetni]);

  // ---- çizim ----
  const calismaSuresi = G.cal.baslangic > 0 ? (G.cal.bitis ?? simdi) - G.cal.baslangic : 0;
  const yanitHatalari = satirlar.filter((s) => s.durum.kod === 'yanit' && s.sonuc !== null && !yenilenen.includes(s.otel.girdi));
  const tumHata = cekimBitti && satirlar.length > 0 && satirlar.every((s) => s.durum.kod === 'yanit');
  const uygunYok = cekimBitti && !tumHata && satirlar.length > 0 && satirlar.every((s) => s.durum.kod !== 'hazir' && s.durum.kod !== 'yanit' && s.durum.kod !== 'uymuyor') && !eskiTalep;
  const hataKarti = yanitHatalari.length > 0 && !tumHata;
  const satisFiyatiYaz = (girdi: string, v: string) => setSatisFiyatlari((m) => { const n = { ...m }; if (v === '') delete n[girdi]; else n[girdi] = v; return n; });
  const gorunenSatirlar: SatirVeri[] = satirlar.map((s) => {
    const slug = s.sonuc?.slug ?? s.otel.slug;
    const seritKimligi = `${kimlik}-serit-${s.otel.girdi}`;
    const acik = acikSeritler.has(s.otel.girdi) && s.sonuc !== null && s.sonuc.durum !== 'HATA' && s.durum.hesap !== null;
    return {
      otel: s.otel,
      sonuc: s.sonuc,
      durum: s.durum,
      secim: s.secim,
      isaretli: G.isaretli.has(s.otel.girdi),
      satisDeger: G.satisFiyatlari[s.otel.girdi] ?? '',
      sistemMinor: s.sistemMinor,
      sistemPara: s.sistemPara,
      enDusuk: enDusukMinor !== null && s.durum.hesap?.toplamMinor === enDusukMinor && s.durum.secilebilir,
      kapak: kapaklar[slug] ?? null,
      seritAcik: acik,
      yenileniyor: yenilenen.includes(s.otel.girdi),
      seritKimligi,
      serit: acik && s.sonuc !== null && s.durum.hesap !== null ? (
        <div onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); seritKapat(s.otel.girdi); } }}>
          <OdaTipiSeridi
            kimlik={seritKimligi}
            otel={s.sonuc.otelAdi ?? s.otel.ad}
            sonuc={s.sonuc}
            hesap={s.durum.hesap}
            devreDisi={saltOkunur || eskiTalep}
            secimDegisti={(grup, no) => secimDegisti(s.otel.girdi, grup, no)}
            hepsineUygula={(grupAnahtari) => {
              const kaynak = s.sonuc?.odaGruplari.find((g) => g.anahtar === grupAnahtari);
              const sec = kaynak?.secenekler.find((x) => x.secenekNo === (s.secim[grupAnahtari] ?? (kaynak === undefined ? null : varsayilanSecenekNo(kaynak))));
              if (sec === undefined || s.sonuc === null) return;
              const yeni: Record<string, string> = { ...s.secim };
              for (const g of s.sonuc.odaGruplari) { const e = g.secenekler.find((x) => x.odaTipi === sec.odaTipi && x.kapasite !== 'UYMUYOR'); if (e !== undefined) yeni[g.anahtar] = e.secenekNo; }
              setSecimler((m) => ({ ...m, [s.otel.girdi]: yeni }));
              setSatisFiyatlari((m) => { const n = { ...m }; delete n[s.otel.girdi]; return n; });
            }}
            varsayilanaDon={() => { setSecimler((m) => { const n = { ...m }; delete n[s.otel.girdi]; return n; }); setSatisFiyatlari((m) => { const n = { ...m }; delete n[s.otel.girdi]; return n; }); }}
            kapat={() => seritKapat(s.otel.girdi)}
            dagilimUygula={dagilimUygula}
          />
        </div>
      ) : null,
    };
  });
  const cekimMetni = cekimBitti
    ? `${M.otelCekildi(satirlar.length, sureMetni(calismaSuresi))} ${sayimMetni(sayim, M.sayim)}`
    : calisiyor ? M.tamamSayisi(Object.keys(G.cal.biten).length, G.cal.oteller.length) : '';
  const n = indeks + 1;

  return (
    <div className="ht-bolum" id={`${kimlik}-panel`} role={coklu ? 'tabpanel' : undefined} aria-labelledby={coklu ? `${kimlik}-sekme` : undefined} hidden={!aktif} data-testid={`${onek}-bolum`}>
      <section
        className="ht-kart"
        aria-labelledby={`${tid}-oteller`}
        onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); fiyatCek(); } }}
        data-testid={`${onek}-oteller`}
      >
        {coklu ? (
          <div className="ht-bolum-ust">
            <h2 id={`${tid}-oteller`}>{etiket}</h2>
            {tarihBolumdeGoster ? (
              <div className="ht-alan ht-bolum-tarih">
                <label htmlFor={`${kimlik}-tarih`}>{M.bolum.tarihler(n)}</label>
                <TarihAraligiSecici id={`${kimlik}-tarih`} testId={`${onek}-tarih`} giris={giris} cikis={cikis} setAralik={(g, c) => { if (!saltOkunur) setAralik(g, c); }} />
                {tarihHata === 'ortusme' ? <span className="ht-hata-yazi" data-testid={`${onek}-tarih-hata`}>{M.bolum.ortusme(n)}</span> : null}
                {tarihHata === 'eksik' && giris === '' ? <span className="ht-ipucu" data-testid={`${onek}-tarih-eksik`}>{M.bolum.eksik(n)}</span> : null}
              </div>
            ) : null}
            <div className="ht-bolum-eylem">
              {silinebilir && !saltOkunur ? (
                silSor ? (
                  <span className="ht-sil-sor" role="group" aria-label={M.bolum.silSor}>
                    <span>{M.bolum.silSor}</span>
                    <button type="button" className="ht-btn ht-btn-t" onClick={sil} data-testid={`${onek}-sil-evet`}>{M.bolum.silEvet}</button>
                    <button type="button" className="ht-btn" onClick={() => setSilSor(false)}>{M.bolum.silVazgec}</button>
                  </span>
                ) : <button type="button" className="ht-btn" onClick={() => setSilSor(true)} data-testid={`${onek}-sil`}>{M.bolum.sil}</button>
              ) : null}
            </div>
          </div>
        ) : null}
        <div className="ht-kart-ust">
          <h2 id={coklu ? undefined : `${tid}-oteller`}>{M.otellerBaslik}</h2>
          <span className="ht-ipucu">{M.otelSayisi(G.oteller.length)}. {M.kisayolIpucu} <kbd>Ctrl</kbd>+<kbd>Enter</kbd> {M.kisayolSon}.</span>
        </div>
        <OtelSecici oteller={G.oteller} durumlar={durumHaritasi} ekle={aramadanEkle} kaldir={otelKaldir} yan={yan} onek={onek} devreDisi={saltOkunur} />
        {saltOkunur ? null : manuelForm !== null ? (
          <ManuelOtelFormu baslangic={manuelForm} gece={gece} kaydet={manuelKaydet} vazgec={() => setManuelForm(null)} onek={onek} />
        ) : (
          <div className="ht-ekle-satir">
            <button type="button" className="ht-btn" onClick={() => setManuelForm(yeniManuelOtel())} data-testid={`${onek}-manuel-ekle`}><Ikon ad="arti" kucuk />{M.manuel.ekle}</button>
            <span className="ht-ipucu">{M.manuel.ipucu}</span>
          </div>
        )}
        <div className="ht-cek-satir">
          <button
            type="button"
            className="ht-btn ht-btn-p ht-btn-b"
            aria-disabled={engelMetni !== null}
            aria-describedby={engelMetni !== null && !calisiyor ? `${tid}-engel` : undefined}
            data-testid={`${onek}-fiyat-cek`}
            onClick={() => fiyatCek()}
          >
            <Ikon ad="yenile" />{calisiyor ? M.fiyatCekiliyor : eskiTalep ? M.fiyatYenidenCek : M.fiyatCek}
          </button>
          {calisiyor ? (
            <div className="ht-ilerleme-kap">
              <div className="ht-ilerleme-satir"><b>{M.tamamSayisi(Object.keys(G.cal.biten).length, G.cal.oteller.length)}</b><span className="ht-ipucu"><Ikon ad="saat" kucuk />{sureMetni(calismaSuresi)} {M.otelBasinaSure}</span></div>
              <div className="ht-bar" aria-hidden="true"><i style={{ width: `${G.cal.oteller.length === 0 ? 0 : Math.round((Object.keys(G.cal.biten).length / G.cal.oteller.length) * 100)}%` }} /></div>
            </div>
          ) : cekimBitti && salt === null ? (
            <>
              <span className="ht-ipucu ht-cekim-ozet" data-testid={`${onek}-cekim-ozet`}>{cekimMetni}</span>
              {sayim.hazir > 0 ? <button type="button" className="ht-btn ht-sagda" onClick={sonuclaraGit} data-testid={`${onek}-sonuclara-git`}>{M.sonuclaraGit(sayim.hazir)}</button> : null}
            </>
          ) : engelMetni !== null ? <span className="ht-ipucu" id={`${tid}-engel`} data-testid={`${onek}-engel`}>{engelMetni}</span> : null}
        </div>
        {baslatHata !== null ? <div className="ht-bilgi-kutu ht-bilgi-uyari"><Ikon ad="uyari" /><span>{baslatHata}</span></div> : null}
        {hataKarti ? (
          <div className="ht-bilgi-kutu ht-bilgi-uyari ht-hata-kart" data-testid={`${onek}-hata-kart`}>
            <Ikon ad="uyari" />
            <div>
              <p><b>{M.hataBaslik(yanitHatalari.length)}: {yanitHatalari.map((s) => s.sonuc?.otelAdi ?? s.otel.ad).join(', ')}.</b> {M.hataNeOldu} {M.hataEtki} {M.hataNeYapmali}</p>
              <button type="button" className="ht-btn" onClick={() => tekYenile(yanitHatalari.map((s) => s.otel.girdi))} data-testid={`${onek}-hata-yeniden`}>{yanitHatalari.length === 1 ? M.yalnizOnuYenidenDene : M.yalnizOnuYenidenDene.replace('onu', 'bunları')}</button>
            </div>
          </div>
        ) : null}
      </section>

      <section className="ht-kart ht-sonuc-kart" id={`${kimlik}-sonuc`} ref={sonucBolumu} aria-labelledby={`${tid}-sonuc`} data-testid={`${onek}-sonuclar`}>
        <div className="ht-kart-ust">
          <h2 id={`${tid}-sonuc`} tabIndex={-1}>{coklu ? `${M.sonuclarBaslik}, ${etiket}` : M.sonuclarBaslik}</h2>
          <span className="ht-ipucu">{coklu ? M.bolum.tekSecim : M.sonuclarIpucu}</span>
        </div>
        {satirlar.length === 0 ? (
          <div className="ht-bos" data-testid={`${onek}-bos`}>
            <strong>{M.sonucBos}</strong>
            <p className="ht-ipucu">{M.sonucBosAcik}</p>
            <span className="ht-sayi-et">{M.sonucBosSayac}</span>
          </div>
        ) : tumHata ? (
          <div className="ht-bilgi-kutu ht-bilgi-tehlike ht-hata-kart" data-testid={`${onek}-tum-hata`}>
            <Ikon ad="uyari" />
            <div><strong>{M.tumHataBaslik}</strong><p>{M.tumHataAcik}</p>
              <button type="button" className="ht-btn" onClick={() => tekYenile(satirlar.map((s) => s.otel.girdi))} data-testid={`${onek}-tumunu-yeniden`}>{M.tumunuYenidenDene}</button>
            </div>
          </div>
        ) : (
          <>
            <p className="ht-ipucu" data-testid={`${onek}-sira-metni`}>{sira !== null && cekimBitti ? M.siraFiyat : M.siraEkleme}</p>
            {siraFarkli ? <button type="button" className="ht-btn" onClick={() => { setSira(fiyatSirasi); dy(M.duy.sira); }} data-testid={`${onek}-sira-guncelle`}>{M.siraGuncelle}</button> : null}
            {eskiTalep ? <div className="ht-bilgi-kutu ht-bilgi-uyari" data-testid={`${onek}-eski-bant`}><Ikon ad="uyari" /><span>{M.eskiAcik}</span></div> : null}
            {uygunYok ? <div className="ht-bilgi-kutu ht-bilgi-uyari" data-testid={`${onek}-uygun-yok`}><Ikon ad="uyari" /><div><strong>{M.uygunYokBaslik}</strong><p>{M.uygunYokAcik}</p></div></div> : null}
            <SonucTablosu
              satirlar={gorunenSatirlar}
              onek={onek}
              tekSecim={coklu}
              grupAdi={`${kimlik}-secim`}
              toplamBasligi={M.kolonToplam(odalar.length)}
              gece={gece ?? 0}
              sirali={sira !== null && cekimBitti}
              salt={saltOkunur}
              isaretle={isaretle}
              seritDegistir={seritDegistir}
              yenile={(g) => tekYenile([g])}
              tabloEtkilesim={etkilesim}
              tusBasildi={tusBasildi}
              satisDegistir={satisFiyatiYaz}
            />
          </>
        )}
        <ManuelOtelListesi
          oteller={G.manuel}
          isaretli={G.manuelIsaretli}
          gece={gece}
          tekSecim={coklu}
          salt={saltOkunur}
          grupAdi={`${kimlik}-secim`}
          onek={onek}
          isaretle={manuelIsaretle}
          duzenle={(id) => setManuelForm(G.manuel.find((x) => x.id === id) ?? null)}
          kaldir={manuelKaldir}
        />
        {isaretliSatirlar.length > 0 ? (
          <div className="ht-satis-fiyatlari" data-testid={`${onek}-satis-fiyatlari`}>
            {satirlar.filter((s) => G.isaretli.has(s.otel.girdi) && s.sonuc !== null && s.durum.kod !== 'eski').map((s) => (
              <SatisFiyatiAlani
                key={s.otel.girdi}
                onek={onek}
                otelAdi={s.sonuc?.otelAdi ?? s.otel.ad}
                slug={s.sonuc?.slug ?? s.otel.slug ?? null}
                sistemMinor={s.sistemMinor}
                para={s.sistemPara}
                deger={G.satisFiyatlari[s.otel.girdi] ?? ''}
                degistir={(v) => satisFiyatiYaz(s.otel.girdi, v)}
                gecerliMinor={s.satisToplamMinor ?? null}
                hatali={s.satisHatali}
                saltOkunur={saltOkunur}
                giris={giris}
                cikis={cikis}
                odalar={odaDuzenleri(odalar)}
                {...(s.odaAdlari === undefined ? {} : { odaAdlari: s.odaAdlari })}
              />
            ))}
          </div>
        ) : null}
        {isaretliSatirlar.length > 0 ? (
          <div className="ht-ek-notlar" data-testid={`${onek}-ek-notlar`}>
            <span className="ht-etiket">{M.ekNotBaslik}</span>
            {isaretliSatirlar.map((s) => (
              <label key={s.otel.girdi} className="ht-ek-not">
                <span>{s.sonuc.otelAdi ?? s.otel.ad}</span>
                <textarea
                  rows={2}
                  maxLength={600}
                  value={G.ekNotlar[s.otel.girdi] ?? ''}
                  disabled={saltOkunur}
                  placeholder={M.ekNotIpucu}
                  data-testid={`${onek}-ek-not-${s.otel.girdi}`}
                  onChange={(e) => { const v = e.target.value; setEkNotlar((m) => ({ ...m, [s.otel.girdi]: v })); }}
                />
              </label>
            ))}
          </div>
        ) : null}
      </section>
    </div>
  );
}
